<template>
  <div class="pie-chart-container"></div>
</template>

<script>
import * as d3 from 'd3';

export default {
  props: ['entries', 'domain', 'size', 'pieWidth', 'pieHeight'],

  data() {
    return {
      width: this.pieWidth || 560,
      height: this.pieHeight || 400,
      radius: this.size / 2 || 90,
      negativeRadius: this.radius && this.innerRadius ? this.radius - this.innerRadius : 50,
    };
  },

  watch: {
    entries: {
      handler(val) {
        this.updatePie(val);
      },

      deep: true,
    },
  },

  mounted() {
    const { radius, width, height, negativeRadius, domain } = this;

    this.percentFormat = d3.format('.0%');
    this.color = d3
      .scaleOrdinal()
      .domain(domain || [])
      .range(['#D60808', '#D8D8D8', '#f1e7ca', '#8b8181', '#9a054a', '#602376', '#7874b5', '#ed700a']);

    this.svg = d3
      .select(this.$el)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`)
      .append('g')
      .attr('transform', 'translate(' + width / 2 + ',' + height / 2 + ')');

    this.svg.append('g').attr('class', 'slices');
    this.svg.append('g').attr('class', 'labels');
    this.svg.append('g').attr('class', 'lines');

    this.arc = d3
      .arc()
      .outerRadius(radius)
      .innerRadius(radius - negativeRadius);

    this.outerArc = d3
      .arc()
      .innerRadius(radius * 1.1)
      .outerRadius(radius * 1.1);

    this.pie = d3
      .pie()
      .padAngle(0.04)
      .sort((a, b) => {
        return b.label - a.label;
      })
      .value(d => {
        return d.count;
      });
    this.updatePie(this.entries);
  },

  methods: {
    getKey(d) {
      return d.data.label.length > 16 ? d.data.label.slice(0, 13) + '...' : d.data.label;
    },

    mergeWithFirstEqualZero(first, second) {
      const secondSet = new Set();

      second.forEach(d => secondSet.add(d.label));

      const onlyFirst = first
        .filter(d => !secondSet.has(d.label))
        .map(d => {
          return { label: d.label, count: 0, percentage: d.percentage };
        });

      return d3.merge([second, onlyFirst]).sort(function (a, b) {
        return d3.ascending(a.label, b.label);
      });
    },

    updatePie(data) {
      const that = this;
      const duration = 1000;
      let data0 = this.svg
        .select('.slices')
        .selectAll('path.slice')
        .data()
        .map(function (d) {
          return d.data;
        });

      if (data0.length === 0) data0 = data;
      let was = this.mergeWithFirstEqualZero(data, data0);
      let is = this.mergeWithFirstEqualZero(data0, data);

      let slice = this.svg.select('.slices').selectAll('path.slice').data(this.pie(was), this.getKey);

      slice
        .enter()
        .insert('path')
        .attr('class', 'slice')
        .style('fill', d => {
          return this.color(d.data.label);
        })
        .each(function (d) {
          this._current = d;
        });

      slice = this.svg.select('.slices').selectAll('path.slice').data(this.pie(is), this.getKey);

      slice
        .transition()
        .duration(duration)
        .attrTween('d', function (d) {
          let interpolate = d3.interpolate(this._current, d);
          let _this = this;
          return function (t) {
            _this._current = interpolate(t);
            return that.arc(_this._current);
          };
        });

      slice = this.svg.select('.slices').selectAll('path.slice').data(this.pie(data), this.getKey);

      slice.exit().transition().delay(duration).duration(0).remove();

      let text = this.svg.select('.labels').selectAll('text').data(this.pie(was), this.getKey);

      text
        .enter()
        .append('text')
        .attr('dy', '.35em')
        .style('opacity', 0)
        .html(d => {
          return `<tspan style="font-weight:400;">${this.percentFormat(d.data.percentage)}</tspan> ${this.getKey(d)}`;
        })
        .each(function (d) {
          this._current = d;
        });

      function midAngle(d) {
        return d.startAngle + (d.endAngle - d.startAngle) / 2;
      }

      text = this.svg.select('.labels').selectAll('text').data(this.pie(is), this.getKey);

      text
        .transition()
        .duration(duration)
        .style('font-weight', '800')
        .style('color', '#3C3C3B')
        .style('opacity', function (d) {
          return d.data.count === 0 ? 0 : 1;
        })
        .attr('alignment-baseline', 'baseline')
        .attrTween('transform', function (d) {
          let interpolate = d3.interpolate(this._current, d);
          let _this = this;

          return function (t) {
            let d2 = interpolate(t);
            _this._current = d2;
            let pos = that.outerArc.centroid(d2);
            pos[0] = that.radius * 2.8 * (midAngle(d2) < Math.PI ? 1 : -1);
            pos[1] -= 10;
            return 'translate(' + pos + ')';
          };
        })
        .styleTween('text-anchor', function (d) {
          let interpolate = d3.interpolate(this._current, d);

          return function (t) {
            let d2 = interpolate(t);
            return midAngle(d2) < Math.PI ? 'end' : 'start';
          };
        });

      text = this.svg.select('.labels').selectAll('text').data(this.pie(data), this.getKey);

      text.exit().transition().delay(duration).remove();

      let polyline = this.svg.select('.lines').selectAll('polyline').data(this.pie(was), this.getKey);

      polyline
        .enter()
        .append('polyline')
        .style('opacity', 0)
        .each(function (d) {
          this._current = d;
        });

      polyline = this.svg.select('.lines').selectAll('polyline').data(this.pie(is), this.getKey);

      polyline
        .transition()
        .duration(duration)
        .style('opacity', function (d) {
          return d.data.count === 0 ? 0 : 1.0;
        })
        .attrTween('points', function (d) {
          let interpolate = d3.interpolate(this._current, d);
          let _this = this;
          return function (t) {
            let d2 = interpolate(t);
            _this._current = d2;
            let pos = that.outerArc.centroid(d2);
            pos[0] = that.radius * 2.9 * (midAngle(d2) < Math.PI ? 1 : -1);
            return [that.outerArc.centroid(d2), pos];
          };
        });

      polyline = this.svg.select('.lines').selectAll('polyline').data(this.pie(data), this.getKey);

      polyline.exit().transition().delay(duration).remove();
    },
  },
};
</script>

<style lang="scss">
@use '@/styles/colors.scss';

.pie-chart-container {
  width: 100%;
  margin-top: 20px;

  svg {
    margin: auto;
    display: block;
  }
}

polyline {
  stroke: #979797;
  stroke-width: 1px;
  fill: none;
}

path.slice {
  stroke-width: 2px;
}

.tv text {
  fill: colors.$white-color;
}
</style>
